<script setup lang="ts">
import CustomRadioButton from '@/core/elements/CustomRadioButton.vue';
import { ECreateClassMode } from 'common/enum/misc.enum';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

type TOption = {
  label: string;
  description: string;
  mode: ECreateClassMode;
};

const { t } = useI18n();

const emit = defineEmits<{
  continue: [mode: ECreateClassMode];
}>();

const selectedMode = ref<ECreateClassMode>(ECreateClassMode.NEW_CLASS);

const newClassOption = ref<TOption>({
  label: t('classes.create'),
  description: t('classes.createDescription'),
  mode: ECreateClassMode.NEW_CLASS,
});

const bumpUpClassOption = ref<TOption>({
  label: t('classes.bumpUp'),
  description: t('classes.bumpUpDescription'),
  mode: ECreateClassMode.SELECT_CLASS,
});

const options = ref<TOption[]>([newClassOption.value, bumpUpClassOption.value]);

const handleOptionSelected = (mode: ECreateClassMode): void => {
  selectedMode.value = mode;
};

const onSubmit = (): void => {
  emit('continue', selectedMode.value);
};

defineExpose({
  onSubmit,
});
</script>

<template>
  <form class="grid h-full w-full grid-rows-[min-content_1fr] px-lg md:px-content" @submit="onSubmit">
    <p class="text-subtitle mb-md">
      {{ $t('classes.bumpUpClassSelectModeDescription') }}
    </p>

    <div class="w-full flex flex-col gap-4 py-md">
      <div
        v-for="option in options"
        :key="option.label"
        :class="[
          'p-md rounded-md outline-inner',
          'flex items-start gap-sm',
          'transition duration-300 cursor-pointer',
          'hover:bg-surface-tertiary active:bg-surface-tertiary',
          selectedMode === option.mode && 'bg-primary/10',
        ]"
        :aria-checked="option.mode === selectedMode"
        tabindex="0"
        role="radio"
        @click="handleOptionSelected(option.mode)"
        @keypress.space.stop="handleOptionSelected(option.mode)"
        @keypress.enter.stop="handleOptionSelected(option.mode)"
      >
        <CustomRadioButton
          name="bump-up-class-mode"
          :model-value="option.mode === selectedMode"
          @update:model-value="handleOptionSelected(option.mode)"
        />
        <div class="flex flex-col">
          <span class="flex items-center gap-sm">
            <span class="font-semibold">{{ option.label }}</span>
          </span>
          <span class="mt-sm">
            {{ option.description }}
          </span>
        </div>
      </div>
    </div>
  </form>
</template>
